import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { AddRounded, DeleteOutlineRounded, DragIndicatorRounded } from '@mui/icons-material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Reusable list editor ───────────────────────────────────────────────────
//
// Generic "list of items with stable ids" editor. Used to author OAPs (program
// learning objectives), OADs (department learning objectives), philosophy
// commitments, and policies. Single source of truth so the UX is consistent
// across the three structural sections that look like
//   [code chip] [text input] [delete]
// and ship the same keyboard shortcuts + add button.

export interface RepeatableListItem {
  id: string;
  code?: string;
  text: string;
}

export interface RepeatableListEditorProps {
  label: string;
  helperText?: string;
  /** Show the "code" input column (e.g. "OAP-1"). Hide for philosophy/policies. */
  showCode?: boolean;
  /** Label of the text column ("Objetivo", "Compromiso", "Política", …). */
  textLabel?: string;
  /** Placeholder for new rows. */
  textPlaceholder?: string;
  items: RepeatableListItem[];
  onChange: (next: RepeatableListItem[]) => void;
  /** Max items (defaults to 30). */
  max?: number;
  /** Per-row prefix used when generating codes automatically (OAP, OAD, OAC). */
  autoCodePrefix?: string;
}

export function RepeatableListEditor({
  label,
  helperText,
  showCode = true,
  textLabel,
  textPlaceholder,
  items,
  onChange,
  max = 30,
  autoCodePrefix,
}: RepeatableListEditorProps): JSX.Element {
  const { t } = useTranslation('sis');
  const resolvedTextLabel = textLabel ?? t('repeatableList.defaultTextLabel');
  const addItem = useCallback(() => {
    if (items.length >= max) return;
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
    const code = autoCodePrefix ? `${autoCodePrefix}-${items.length + 1}` : '';
    onChange([...items, { id, ...(showCode && code ? { code } : {}), text: '' }]);
  }, [items, max, autoCodePrefix, showCode, onChange]);

  const updateItem = (id: string, patch: Partial<RepeatableListItem>) => {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id: string) => {
    onChange(items.filter((it) => it.id !== id));
  };

  return (
    <Stack spacing={1}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
        {helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </Box>

      <Stack spacing={1}>
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
            {t('repeatableList.empty')}
          </Typography>
        )}
        {items.map((item) => (
          <Stack key={item.id} direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                pt: 1,
                color: 'text.disabled',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <DragIndicatorRounded fontSize="small" />
            </Box>
            {showCode && (
              <TextField
                size="small"
                placeholder={
                  autoCodePrefix ? `${autoCodePrefix}-1` : t('repeatableList.codePlaceholder')
                }
                value={item.code ?? ''}
                onChange={(e) => updateItem(item.id, { code: e.target.value })}
                sx={{ width: 110, fontFamily: 'monospace' }}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            )}
            <TextField
              size="small"
              placeholder={textPlaceholder ?? resolvedTextLabel}
              value={item.text}
              onChange={(e) => updateItem(item.id, { text: e.target.value })}
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
            />
            <Tooltip title={t('repeatableList.delete')}>
              <IconButton
                size="small"
                onClick={() => removeItem(item.id)}
                sx={{ mt: 0.5 }}
                aria-label={t('repeatableList.deleteItemAria')}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      <Box>
        <Button
          size="small"
          startIcon={<AddRounded />}
          onClick={addItem}
          disabled={items.length >= max}
          sx={{ textTransform: 'none' }}
        >
          {t('repeatableList.add')}
        </Button>
        {items.length >= max && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {t('repeatableList.maxReached', { max })}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
